// PWA-Status + Service-Worker-Registration. Wird einmal beim Boot
// (main.ts) angetriggert; die Settings-Sektion "Push" liest die
// reaktiven Werte fuer den iOS-Hinweis und den Permission-Status.
//
// Wichtig iOS: Web-Push funktioniert nur in einer installierten PWA
// (vom Home-Bildschirm gestartet, navigator.standalone === true).
// Im normalen Safari-Tab ist Notification.permission permanent
// "default" -- der Request-Button bleibt sichtbar, aber der User muss
// die App erst installieren. Die UI erklaert das.

import { SvelteMap } from "svelte/reactivity";
import { deleteJSON, getJSON, postJSON } from "./api";
import { fetchVersion } from "./version-fetch";
import { ladeNeu } from "./neuladen";
import { neuladenGehalten } from "./neuladen-halt";

// Reaktiver Zustand. SvelteMap, damit $derived in Komponenten
// automatisch re-rendert wenn sich Werte aendern (Pattern aus
// i18n.svelte.ts).
const _state = new SvelteMap<string, boolean | string>([
  ["registered", false],
  ["supported", false],
  ["standalone", false],
  ["ios", false],
  ["permission", "default"],
]);

let _started = false;

export function startPWA(): void {
  if (_started) return;
  _started = true;

  // Auto-Reload nach Deploy -- unabhaengig von PWA/SW-Support (nur fetch +
  // reload), daher vor dem supported-Early-Return.
  wireVersionCheck();

  const ua = navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
  _state.set("ios", ios);

  // navigator.standalone existiert nur in iOS-Safari; auf Android prueft
  // man display-mode. Beides abdecken.
  const standalone =
    // iOS-Safari
    (typeof (navigator as unknown as { standalone?: boolean }).standalone ===
      "boolean" &&
      (navigator as unknown as { standalone?: boolean }).standalone === true) ||
    // Standard
    window.matchMedia?.("(display-mode: standalone)").matches ||
    false;
  _state.set("standalone", standalone);

  const supported =
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  _state.set("supported", supported);

  if (typeof Notification !== "undefined") {
    _state.set("permission", Notification.permission);
  }

  if (!supported) return;

  // Registration laeuft asynchron im Hintergrund; UI-Status reflektiert
  // das, wenn fertig.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!expectReload) return;
    expectReload = false;
    ladeNeu("version");
  });

  void navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((reg) => {
      _state.set("registered", true);
      // Cold-Start mit bereits wartendem SW: der Inhalt ist gerade frisch
      // vom Netz gekommen, ein "Neu laden"-Hinweis waere hier redundant.
      // Den Wartenden still uebernehmen lassen (Cache-Konsistenz), ohne
      // Reload. Das Banner bleibt dem WARMEN Fall vorbehalten: App laeuft,
      // waehrenddessen kommt ein Deploy.
      if (reg.waiting && navigator.serviceWorker.controller) {
        reg.waiting.postMessage({ type: "skipWaiting" });
      }
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          // installed + es gibt schon einen Controller = echtes Update
          // (nicht die Erstinstallation). Nur merken -- wann neu geladen
          // wird, entscheidet der Versions-Check.
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            waitingSW = nw;
          }
        });
      });
    })
    .catch((err) => {
      console.warn("[pwa] sw register failed", err);
    });

  // Deep-Link-Handler: wenn der SW beim Notification-Click die App schon
  // offen findet, schickt er postMessage({type:"deeplink",target}) statt
  // einer Full-Page-Navigation. Wir setzen daraufhin den Hash, der
  // Router-Listener im Window picked das auf.
  navigator.serviceWorker.addEventListener("message", (ev) => {
    const data = ev.data;
    if (!data || typeof data !== "object") return;
    if (data.type !== "deeplink") return;
    applyDeeplink(typeof data.target === "string" ? data.target : "");
  });

  // Cold-Boot-Pfad: iOS startet die PWA bei Banner-Tap immer ueber
  // manifest.start_url ("/"). Der SW hat in dem Fall den Ziel-Pfad in
  // den Cache geschrieben -- den ziehen wir hier raus und setzen den
  // Hash, BEVOR die App rendert.
  void consumePendingDeeplink();

  // Selbstcheck des eigenen Push-Abos (Schicht 4): der kaputte Push-
  // Kanal kann sich nicht selbst melden -> die App fragt beim Boot
  // (nach SW-Ready), bei Rueckkehr in den Vordergrund und nach jedem
  // WS-(Re-)Connect (= frisch eingeloggt oder Deploy) aktiv nach, wie
  // es um IHR eigenes Abo steht. PushHealthBanner.svelte zeigt dann
  // in-app "Neu einrichten?", wenn hier vermutlich nichts ankommt.
  setTimeout(() => void pwaCheckPushSelf(), 4000);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void pwaCheckPushSelf();
  });
  window.addEventListener("v2:ws-open", () => void pwaCheckPushSelf());
}

// ── Selbstcheck + Neu-Einrichten (Benachrichtigungs-Gesundheit) ─────────
// Portiert aus home-dash. deliver: broken | silent | unknown -> Banner.

export const pushSelf = $state<{
  loaded: boolean;
  deliver: string; // broken|confirmed|silent|unconfirmed|untested|unknown|none
  problem: boolean; // hier kommt vermutlich nichts an -> Banner sichtbar
  busy: boolean; // Neu-Einrichten laeuft
  error: string; // letzter Fehler beim Neu-Einrichten (Banner zeigt ihn)
}>({ loaded: false, deliver: "", problem: false, busy: false, error: "" });

let selfCheckInflight = false;

export async function pwaCheckPushSelf(): Promise<void> {
  if (selfCheckInflight) return;
  selfCheckInflight = true;
  try {
    if (!pwaSupported()) return;
    const reg = await getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Nicht abonniert -> kein Banner (der normale Aktivieren-Flow
      // wohnt in der Karte „Benachrichtigungen" im Einstellungsfenster).
      pushSelf.loaded = true;
      pushSelf.deliver = "none";
      pushSelf.problem = false;
      return;
    }
    const r = await postJSON<{ deliver: string; found: boolean }>(
      "/v2-api/push/web/self",
      { endpoint: sub.endpoint },
    );
    pushSelf.deliver = r.deliver;
    // Nur STARKE Signale warnen (kein Fehlalarm bei frisch/ungetestet):
    // broken (Dienst weist ab), silent (tagelang nie bestaetigt),
    // unknown (Server kennt das Abo nicht mehr).
    pushSelf.problem =
      r.deliver === "broken" || r.deliver === "silent" || r.deliver === "unknown";
    pushSelf.loaded = true;
  } catch {
    // Nicht kritisch (z.B. noch nicht eingeloggt) -- lieber kein Banner
    // als ein falscher Alarm.
  } finally {
    selfCheckInflight = false;
  }
}

// pwaResubscribe richtet den Push-Kanal dieses Geraets VOLL neu ein:
// bestehendes Abo sauber verwerfen (Server-Eintrag + Browser-Abo) und
// frisch subscriben. Das Verwerfen ist Pflicht -- ein Abo an einem
// alten/fremden VAPID-Key laesst Chrome/Android beim subscribe sonst
// mit InvalidStateError abbrechen. Ermittelt seinen Support selbst und
// haengt an keinem Seiten-Init (Lesson aus home-dash: der Banner
// erscheint app-weit, auch auf Seiten ohne Push-Setup-Code).
export async function pwaResubscribe(): Promise<void> {
  if (pushSelf.busy) return;
  pushSelf.busy = true;
  pushSelf.error = "";
  try {
    if (!pwaSupported()) {
      throw new Error("Dieses Gerät unterstützt keine Web-Push-Benachrichtigungen.");
    }
    const perm = await pwaRequestPermission();
    if (perm !== "granted") {
      throw new Error(
        perm === "denied"
          ? "Benachrichtigungen sind im Browser blockiert."
          : "Benachrichtigungen wurden nicht erlaubt.",
      );
    }
    const reg = await getRegistration();
    if (!reg) throw new Error("Service Worker nicht bereit.");
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await deleteJSON(
        "/v2-api/push/web/subscribe?endpoint=" + encodeURIComponent(existing.endpoint),
      ).catch(() => {});
      await existing.unsubscribe().catch(() => {});
    }
    const vapid = await getJSON<VAPIDResponse>("/v2-api/push/web/vapid-public-key");
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        vapid.public_key,
      ) as unknown as BufferSource,
    });
    await postJSON("/v2-api/push/web/subscribe", sub.toJSON());
    // Frisch eingerichtet -> Warnung weg; Zustand bis zum naechsten
    // Selbstcheck optimistisch "unconfirmed".
    pushSelf.problem = false;
    pushSelf.deliver = "unconfirmed";
  } catch (e) {
    pushSelf.error = e instanceof Error ? e.message : String(e);
  } finally {
    pushSelf.busy = false;
  }
}

// ── Auto-Reload nach Deploy ─────────────────────────────────────────────
// Wahrheit ist der Build-Vergleich: Server (/v2-api/version, no-store) gegen
// das eingebaute __APP_BUILD__. Getriggert v.a. beim WS-Reconnect (= Deploy-
// Signal, ein Restart trennt den Socket) plus Boot, Rueckkehr in den
// Vordergrund und 5-min-Intervall. Waehrend aktiver Nutzung wird ein Update
// nur VORGEMERKT und beim naechsten Verstecken/Oeffnen gezogen -- nie unterm
// Finger weg. Loop-Schutz (localStorage): je Server-Build hoechstens EIN
// Auto-Reload, damit ein stoerrischer Cache keine Endlosschleife ausloest.
const RELOADED_FOR_KEY = "v2-auto-reloaded-for";
let pendingBuild = "";

function lsGet(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}
function lsSet(k: string, v: string): void {
  try { localStorage.setItem(k, v); } catch { /* Privatmodus o.ae. */ }
}

function wireVersionCheck(): void {
  if (__APP_BUILD__ === "dev") return; // Vite-Dev-Server: kein Rauschen
  // KEIN Neuladen bei vite:preloadError (bis 20.10.2026 hier): das
  // Ereignis kommt auch, wenn prefetchAllPages im Hintergrund einen
  // Baustein nicht laden kann -- und lud dann die ganze Seite neu. Tims
  // Safari hatte sich um 19:23 eine 502 (Deploy-Neustart) für
  // Auswertungen-*.js gemerkt; jeder Start lief beim Vorladen darauf auf
  // und lud 30-40 s später neu, immer wieder (Tim: „Das Homelab lädt
  // periodisch die Seite neu"). Ohne preventDefault wirft der Import
  // weiter: wer eine Seite wirklich öffnet (App.svelte, PageHost), lädt
  // dann selbst neu, das Vorladen schluckt den Fehler.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      // App unsichtbar -> vorgemerktes Update lautlos ziehen.
      if (pendingBuild) applyPendingUpdate();
      return;
    }
    void checkServerVersion(true); // Rueckkehr = Oeffnungs-Moment
  });
  // WS-Reconnect ist DAS Deploy-Signal (ws-hub feuert v2:ws-open).
  window.addEventListener("v2:ws-open", () => void checkServerVersion(false));
  setInterval(() => void checkServerVersion(false), 5 * 60 * 1000);
  setTimeout(() => void checkServerVersion(true), 3000); // Boot
}

async function checkServerVersion(openingMoment: boolean): Promise<void> {
  try {
    // Geteilte Leitung (version-fetch): der Seitenfuss fragt dieselbe
    // Adresse. Beim Boot fielen die Abrufe sonst doppelt an.
    const v = await fetchVersion();
    if (!v) return;
    if (!v.build_time || v.build_time === "unknown" || v.build_time === __APP_BUILD__) return;
    if (lsGet(RELOADED_FOR_KEY) === v.build_time) return; // schon einmal reloaded
    pendingBuild = v.build_time;
    if (openingMoment || document.hidden) applyPendingUpdate();
  } catch {
    // Netzfehler -> naechster Trigger versucht es wieder
  }
}

// ── Tote Lazy-Chunks nach einem Deploy ─────────────────────────────────
// Jeder Deploy dreht die Content-Hashes; die alten Chunk-Dateien sind aus
// dem Binary verschwunden. Eine Sitzung, die WAEHREND eines Deploys offen
// bleibt, laeuft bewusst auf der alten Shell weiter (nie unterm Finger
// weg). Das traegt aber nur, solange die alte Shell noch alles findet,
// was sie braucht -- eine Page, deren Chunk noch NIE geladen wurde, zeigt
// auf eine Datei, die es nicht mehr gibt. Ihr import() scheitert; ohne
// Behandlung blieb die vorherige Seite unterm neuen Breadcrumb stehen
// (Tim 15.07., Automatisierungen; gleicher Vorfall in home-dash b3ed0cd).
//
// Der tote Chunk ist der eine Moment, in dem die alte Shell nicht weiter-
// kann -- und der Nutzer wartet ohnehin auf einen Seitenwechsel, also auf
// einen Ladevorgang. Loop-Schutz: hoechstens EIN Chunk-Reload pro 30 s.
// Liegt es nicht am Deploy (Chunk auch nach dem Reload weg), bleibt die
// App ruhig und App.svelte zeigt seine Fehlerkarte -- statt endlos zu
// reloaden.
// Wartender Service-Worker aus einem Deploy. Seit der SW die App-Shell
// cached (SW_VERSION 4) reicht ein blosses reload NICHT mehr: der alte
// SW wuerde die alte Shell aus seinem Cache ausliefern. Der neue muss
// erst uebernehmen (skipWaiting), dann laedt controllerchange einmal neu.
let waitingSW: ServiceWorker | null = null;
// Nur ein bewusst angestossenes Update darf reloaden. Ein stiller
// Cold-Start-Wechsel (Inhalt kam per network-first ohnehin frisch) darf
// es NICHT -- sonst blitzt die App bei jedem Start neu auf.
let expectReload = false;

const CHUNK_RELOAD_KEY = "pwa-chunk-reload-ts";
const CHUNK_RELOAD_GRACE = 30_000;
let chunkReloading = false;

// reloadForBrokenChunk: true = uebernommen (Reload laeuft), false =
// Aufrufer muss den Fehler selbst zeigen.
export function reloadForBrokenChunk(detail = ""): boolean {
  if (chunkReloading) return true; // schon unterwegs (preloadError + import-catch)
  // Ungespeicherte Eingaben in einem anderen Tab (lib/neuladen-halt.ts):
  // nicht neu laden -- der Aufrufer zeigt seine Fehlerkarte, deren Knopf
  // lädt neu, und davor fragt der Browser (Repo-Prüfung 21.10.2026, zweite
  // Runde).
  if (neuladenGehalten().length > 0) return false;
  const last = Number(lsGet(CHUNK_RELOAD_KEY) || "0");
  if (Date.now() - last < CHUNK_RELOAD_GRACE) return false;
  chunkReloading = true;
  lsSet(CHUNK_RELOAD_KEY, String(Date.now()));
  ladeNeu("baustein", detail);
  return true;
}

// Seit der SW die App-Shell cached, muss ein wartender SW ERST
// uebernehmen -- sonst liefert der alte weiter seine alte Shell aus.
// controllerchange loest den Reload dann genau einmal aus; der Timeout
// ist nur die Rueckfallebene, falls die Uebernahme ausbleibt.
function applyPendingUpdate(): void {
  if (!pendingBuild) return;
  // Ein Editor hält ungespeicherte Änderungen (lib/neuladen-halt.ts): das
  // Update bleibt vorgemerkt und zieht beim nächsten Verstecken nach der
  // Freigabe. Bis 21.10.2026 lud schon das Wechseln in eine andere App neu
  // und verwarf die Eingaben (Repo-Prüfung, zweite Runde).
  if (neuladenGehalten().length > 0) return;
  lsSet(RELOADED_FOR_KEY, pendingBuild);
  pendingBuild = "";
  if (waitingSW) {
    expectReload = true;
    waitingSW.postMessage({ type: "skipWaiting" });
    waitingSW = null;
    setTimeout(() => ladeNeu("version"), 2000);
    return;
  }
  ladeNeu("version");
}

function applyDeeplink(target: string): void {
  if (!target) return;
  const hash = target.startsWith("#") ? target.slice(1) : target;
  if (window.location.hash.slice(1) === hash) return;
  window.location.hash = hash;
}

const PENDING_DEEPLINK_CACHE = "homelab-pending-deeplink";
const PENDING_DEEPLINK_KEY = "/__pending_deeplink";
// Eintraege aelter als 60 s ignorieren -- ein stale Wert aus einer
// frueheren Session soll uns beim normalen App-Start nicht woanders
// hin springen lassen.
const PENDING_DEEPLINK_TTL_MS = 60_000;

async function consumePendingDeeplink(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const cache = await caches.open(PENDING_DEEPLINK_CACHE);
    const r = await cache.match(PENDING_DEEPLINK_KEY);
    if (!r) return;
    await cache.delete(PENDING_DEEPLINK_KEY);
    const data = (await r.json()) as { target?: string; ts?: number };
    if (!data || typeof data.target !== "string" || !data.target) return;
    if (typeof data.ts !== "number" || Date.now() - data.ts > PENDING_DEEPLINK_TTL_MS) {
      return;
    }
    applyDeeplink(data.target);
  } catch (err) {
    console.warn("[pwa] pending deeplink read failed", err);
  }
}

export function pwaRegistered(): boolean {
  return Boolean(_state.get("registered"));
}

export function pwaSupported(): boolean {
  return Boolean(_state.get("supported"));
}

export function pwaStandalone(): boolean {
  return Boolean(_state.get("standalone"));
}

export function pwaIsIOS(): boolean {
  return Boolean(_state.get("ios"));
}

export function pwaPermission(): NotificationPermission {
  const v = _state.get("permission");
  if (v === "granted" || v === "denied" || v === "default") return v;
  return "default";
}

// requestPermission triggert den Browser-Prompt. Auf iOS verlangt es
// einen User-Gesture-Kontext (Click-Handler) -- der Aufrufer muss das
// also aus einem onclick heraus aufrufen.
export async function pwaRequestPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  const result = await Notification.requestPermission();
  _state.set("permission", result);
  return result;
}

// Web-Push Subscribe-Flow (Slice 1). Holt den VAPID-Public-Key vom
// Backend, ruft pushManager.subscribe und meldet die Subscription
// beim Backend an. Idempotent -- wenn der Browser schon eine
// Subscription hat, wird sie nur erneut beim Server registriert.
//
// Auf iOS muss die App standalone laufen, sonst lehnt pushManager
// die subscribe-Anfrage ab. Aufrufer pruefen vorher pwaStandalone()
// (oder ignorieren das und lassen den nativen Fehler durchschlagen).

interface VAPIDResponse {
  public_key: string;
  subject: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  // Web-Push VAPID-Keys kommen im URL-safe Base64-Format ohne Padding;
  // pushManager.subscribe will aber rohe Bytes als BufferSource.
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg ?? null;
}

export async function pwaSubscribe(): Promise<void> {
  if (!pwaSupported()) {
    throw new Error("Browser unterstützt keine Push-Benachrichtigungen.");
  }
  // Permission muss erteilt sein. Aufrufer sollte vorher
  // pwaRequestPermission() im User-Gesture-Kontext aufgerufen haben.
  if (Notification.permission !== "granted") {
    const p = await pwaRequestPermission();
    if (p !== "granted") {
      throw new Error("Benachrichtigungen wurden nicht erlaubt.");
    }
  }
  const reg = await getRegistration();
  if (!reg) throw new Error("Service Worker nicht bereit.");

  const vapid = await getJSON<VAPIDResponse>(
    "/v2-api/push/web/vapid-public-key",
  );
  const appServerKey = urlBase64ToUint8Array(vapid.public_key);

  // Bestehende Subscription beibehalten wenn moeglich -- iOS kann mit
  // wiederholten subscribe-Calls hin und wieder Probleme machen.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Spec verlangt BufferSource. Manche Safari-Versionen sind
      // strikt -- explizit den ArrayBuffer reichen.
      applicationServerKey: appServerKey as unknown as BufferSource,
    });
  }
  const payload = sub.toJSON();
  await postJSON("/v2-api/push/web/subscribe", payload);
}

export async function pwaUnsubscribe(): Promise<void> {
  const reg = await getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (err) {
    console.warn("[pwa] unsubscribe(browser) failed", err);
  }
  // Backend-Eintrag immer wegputzen, auch wenn der Browser-Call
  // fehlschlaegt -- der Endpoint wird dann eh nicht mehr beliefert.
  await deleteJSON(
    "/v2-api/push/web/subscribe?endpoint=" + encodeURIComponent(endpoint),
  );
}

// pwaCurrentEndpoint liefert die endpoint-URL der aktuellen Browser-
// Subscription oder null. Wird vom Settings-UI fuer "subscribed" vs
// "nicht subscribed" gebraucht.
export async function pwaCurrentEndpoint(): Promise<string | null> {
  const reg = await getRegistration();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  return sub ? sub.endpoint : null;
}

// pwaSendTest stoesst eine Test-Notification an dieses Geraet vom
// Backend aus an. Server findet die zugehoerige Subscription ueber die
// Session und sendet gezielt dorthin. Bei Push-Service-Fehler kommt
// ok=false + error im Body (statt 5xx), damit Cloudflare nicht seine
// eigene Error-Page rendert.
interface TestResponse {
  ok: boolean;
  error?: string;
}
export async function pwaSendTest(): Promise<void> {
  const res = await postJSON<TestResponse>("/v2-api/push/web/test", {});
  if (!res.ok) {
    throw new Error(res.error || "unbekannter Fehler");
  }
}

// Cross-device-Subscription-Mgmt: Liste, Test-an-anderes-Geraet,
// Revoke-by-ID.

export interface PushSubscriptionView {
  id: number;
  device_id: number;
  device_name: string;
  user_agent: string;
  created_at: string;
  last_used_at: string;
  origin: string;
  is_current: boolean;
  // Benachrichtigungs-Gesundheit (Send-Health + ACK, Mig 097).
  deliver: "broken" | "confirmed" | "silent" | "unconfirmed" | "untested";
  health: "unknown" | "ok" | "broken";
  last_error: string;
  last_confirm: string;
}

export async function pwaListSubscriptions(): Promise<PushSubscriptionView[]> {
  return await getJSON<PushSubscriptionView[]>(
    "/v2-api/push/web/subscriptions",
  );
}

export async function pwaTestSubscription(id: number): Promise<void> {
  const res = await postJSON<TestResponse>(
    `/v2-api/push/web/subscriptions/${id}/test`,
    {},
  );
  if (!res.ok) {
    throw new Error(res.error || "unbekannter Fehler");
  }
}

export async function pwaRevokeSubscription(id: number): Promise<void> {
  await deleteJSON(`/v2-api/push/web/subscriptions/${id}`);
}

// pwaDropLocalSubscription verwirft das Browser-seitige Push-Abo DIESES
// Geräts (ohne Server-Call). Gehört zum Entfernen des eigenen Geräts
// aus der Geräteliste: der Server-Revoke allein liesse das Browser-Abo
// weiterleben -- die Status-Karte zeigt dann "aktiv" und der nächste
// Selbstcheck meldet "Abo unbekannt" (Banner-Fehlalarm). Danach den
// Selbstcheck-Zustand mit zurücksetzen.
export async function pwaDropLocalSubscription(): Promise<void> {
  try {
    const reg = await getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch (err) {
    console.warn("[pwa] local unsubscribe failed", err);
  } finally {
    pushSelf.deliver = "none";
    pushSelf.problem = false;
    pushSelf.error = "";
  }
}
